import {Component, Expressions} from '@packages/systems/wfdl';
import {Expression, ExpressionElement} from '@packages/systems/wfdl-ast';
import type {Qualified} from '@packages/systems/wfdl-Qualified';
import {namesEqual} from '@packages/systems/wfdl-Qualified';
import type {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {
  getElementFromNodeInInstanceNativeId,
  getOutermostSymbolInstance,
} from '@packages/utilities/platform';

/**
 * Given a native id or native id path, returns the selected element for optimization purposes
 * if you need to know if the selected element or its component instance is in an experience.
 *
 * @param nativeId - The native ID of the selected node
 * @param nativeIdPath - The native ID path for elements within component instances
 * @param componentMap - The component map
 * @param currentComponentType - The current component type
 * @param currentComponentRender - The current component render expression
 * @returns The selected element, or null if not found
 */
export function getSelectedElementForOptimization(
  nativeId: NativeId | null,
  nativeIdPath: NativeId[] | null | undefined,
  componentMap: Component.ComponentMap,
  currentComponentType: Qualified<Component.ComponentName>,
  currentComponentRender: Expression | null
): ExpressionElement | null {
  if (!currentComponentRender) {
    return null;
  }

  // Get the element from nativeId/nativeIdPath
  const element =
    nativeIdPath && nativeIdPath.length > 0
      ? getElementFromNodeInInstanceNativeId(nativeIdPath, componentMap)
      : nativeId
        ? Expressions.getElementById(currentComponentRender, nativeId)
        : null;

  if (!element) {
    return null;
  }

  // If this is a component prop get the component instance
  const componentInstance =
    nativeIdPath && nativeIdPath.length > 0
      ? getOutermostSymbolInstance(nativeIdPath, componentMap)
      : null;

  const componentInstanceIsCurrentComponent =
    componentInstance !== null &&
    namesEqual(componentInstance.type, currentComponentType);

  // If this is within a component instance, check that the instance
  // is not currently within another experience.
  const selectedElement =
    componentInstance && !componentInstanceIsCurrentComponent
      ? componentInstance
      : element;

  return selectedElement;
}
